import * as PIXI from 'pixi.js';
import * as setup from 'setup';
import Entity from '../../Entity/Entity';
import Map from '../Map';

export default class Debug extends PIXI.Container {

    gameState: GameState;
    map: Map;
    mouse?: DebugItem;

    colors: { [key: string]: number } = {
        'ant-explorer': 0x00AAFF,
        'ant-queen': 0x00FFFF,
        'ant-warrior': 0x0055FF,
        'ant-worker': 0x0000FF,
        'danger-anteater': 0xFF0000,
        'danger-antWarrior': 0xFF5500,
        'danger-antWorker': 0xFF0000,
        'danger-lawnmower': 0xFF0000,
        'danger-magnifier': 0xFF0000,
        'danger-spider': 0xFF0000,
        'default': 0xFFFFFF,
        'food': 0x00FF00,
        'nature': 0xCCCCCC,
        'nature-antColony': 0x5555FF,
        'nature-dangerColony': 0xFF5555,
    };
    items: DebugItem[] = [];
    tickItems: DebugItem[] = [];

    constructor(gameState: GameState) {
        super();

        // Initialize
        this.sortableChildren = true;

        // Check debug
        if (gameState.settings.debugMap) {
            // Create grid
            const grid = new PIXI.Graphics();
            this.addChild(grid);
            grid.zIndex = 0;
            for (let i = 0; i < setup.map.cells.wide; i++) {
                grid.rect(i * setup.map.cells.size, 0, 1, setup.map.cells.size * setup.map.cells.wide);
                grid.rect(0, i * setup.map.cells.size, setup.map.cells.size * setup.map.cells.wide, 1);
            }
            grid.fill({ color: this.colors.default, alpha: 0.1 });

            // Create mouse
            this.mouse = this.createItem();
            this.updateItem(this.mouse);
        }

        // Add listeners
        this.addListener('added', this.handleAdded);
    }

    handleAdded = () => {
        this.map = this.parent as Map;

        // Check mouse
        if (this.mouse) {
            this.map.addListener('mousemove', this.handleMouseMove);
        }
    }

    updateGameState = (gameState: GameState) => {
        this.gameState = gameState;
        this.updateItems();
    }

    updateItems = () => {
        const { debugAnts, debugDangers, debugFoods, debugNature } = this.gameState.settings;

        // Get entities
        const entities = this.map.entities.children.map(entity => entity as Entity)
            .filter(entity => !entity.destroyed && ((entity.category === 'ant' && debugAnts) || (entity.category === 'danger' && debugDangers) || (entity.category === 'food' && debugFoods) || (entity.category === 'nature' && debugNature)));

        // Create items if needed
        for (let i = this.items.length; i < entities.length; i++) {
            const item = this.createItem();
            this.items.push(item);
        }

        // Remove items if needed
        while (this.items.length > entities.length) {
            const item = this.items.pop() as DebugItem;
            this.removeChild(item.container);
            item.container.destroy();
        }

        // Update items
        this.items.forEach((item, index) => this.updateItem(item, entities[index]));
        const newTickItems = this.items.filter(item => item.entity.category === 'ant' || item.entity.category === 'danger');
        if (newTickItems.length) {
            if (!this.tickItems.length) {
                PIXI.Ticker.shared.add(this.handleTick);
            }
        } else if (this.tickItems.length) {
            PIXI.Ticker.shared.remove(this.handleTick);
        }
        this.tickItems = newTickItems;

        // Update mouse
        if (this.mouse) {
            this.mouse.container.zIndex = this.items.length;
        }
    }

    createItem = () => {
        // Create container
        const container = new PIXI.Container();
        this.addChild(container);

        // Create frame
        const frame = new PIXI.Graphics();
        container.addChild(frame);

        // Create label
        const label = new PIXI.Text();
        container.addChild(label);
        label.style.fontSize = 12;

        // Create radius
        const radius = new PIXI.Graphics();
        container.addChild(radius);

        // Get item
        return {
            container,
            frame,
            label,
            radius,
        } as DebugItem;
    }

    updateItem = (item: DebugItem, entity?: Entity) => {
        if (!entity || item.entity !== entity) {
            // Set target entity
            item.entity = entity;

            // Get color and size
            const colorKey = entity ? `${entity.category}-${entity.type}` : 'default';
            item.color = Object.entries(this.colors).filter(([key]) => colorKey.startsWith(key)).pop()?.[1] || 0;
            item.size = setup.map.cells.size * (entity?.size || 1);

            // Update frame
            item.frame.clear();
            item.frame.rect(0, 0, item.size, item.size);
            item.frame.fill({ color: item.color, alpha: 1 });
            item.frame.rect(2, 2, item.size - 4, item.size - 4);
            item.frame.cut();
            item.frame.x = -item.size / 2;
            item.frame.y = -item.size / 2;

            // Update label
            item.label.x = item.frame.x;
            item.label.y = item.frame.y + item.frame.height;
            item.label.style.fill = item.color;
        }

        // Update container
        item.container.alpha = !entity || entity.active ? 1 : 0.5;
        item.container.x = entity?.x;
        item.container.y = entity?.y;

        // Update label
        if (entity) {
            item.label.text = entity.type;
            if (entity.category === 'ant' && entity.type === 'worker') {
                item.label.text += `: ${item.entity.carryAmount}`;
            } else if (entity.category === 'ant' && entity.type === 'explorer' && entity.mutation) {
                item.label.text += `-${entity.mutation}`;
            } else if (entity.category === 'food') {
                item.label.text += `: ${item.entity.caloriesRemaining}`;
            }

        } else {
            item.label.x += 4;
            item.label.y -= item.label.height + 2;
        }

        // Update radius
        const radius = (entity?.getDebugRadius() || 0) * setup.map.cells.size;
        if (!radius || item.radius.width / 2 !== radius) {
            item.radius.clear();
            if (radius) {
                item.radius.circle(0, 0, radius);
                item.radius.fill({ color: item.color, alpha: entity?.category === 'ant' || entity?.category === 'danger' ? 0.3 : 0.7 });
                item.radius.circle(0, 0, radius - 2);
                item.radius.cut();
            }
        }
    }

    handleMouseMove = (e: PIXI.FederatedPointerEvent) => {
        if (this.gameState && this.mouse) {
            // Get position based on mouse 
            const max = setup.map.cells.size * setup.map.cells.wide * this.map.scale.x;
            const x = Math.floor((e.global.x - this.map.x) / max * setup.map.cells.wide);
            const y = Math.floor((e.global.y - this.map.y) / max * setup.map.cells.wide);

            // Update mouse
            const blocked = this.gameState.gridMatrix[y]?.[x];
            this.mouse.container.x = setup.map.cells.size * (x + 0.5);
            this.mouse.container.y = setup.map.cells.size * (y + 0.5);
            this.mouse.label.text = `${x},${y} ${blocked ? 'X' : ' '}`;
        }
    }

    handleTick = () => {
        // Update tickItems positions
        this.tickItems.forEach(item => {
            if (!item.entity.destroyed) {
                item.container.x = item.entity.x;
                item.container.y = item.entity.y;
            }
        });
    }

}